import { store } from '../../store/store';
import { getShift, shiftBatchApprove, shiftBatchBillable, updateShift, shiftApprove, shiftBillable } from '../shifts'
import { showSnack } from '../snacks/snackService';
import { TimeSheetItem, TimeSheetItemStatus, TimeSheetItemType } from './types';
import { asAbsence, asShift, isAbsenceDirty, isShiftDirty } from './utils';
import { TiminatorAbsenceType, TiminatorType } from '../types/types';
import { absenceBatchApprove, getAbsence, updateAbsence, absenceApprove } from '../absences';

export const UpdateShiftFromItem = async (item: TimeSheetItem, getType: () => TiminatorType): Promise<void> => {
    try {
        const shift = await store.dispatch(getShift(item.itemId)).unwrap();
        if (isShiftDirty(item, shift)) {
            const type = getType();
            await store.dispatch(updateShift(asShift(item, shift, type))).unwrap();
        }
    } catch (error) {
        showSnack(`Unable to update shift ${formatScheduleError(error)}`, 'warning')
    }
}

export const UpdateAbsenceFromItem = async (item: TimeSheetItem, getType: () => TiminatorAbsenceType | undefined): Promise<void> => {
    try {
        const absence = await store.dispatch(getAbsence(item.itemId)).unwrap();
        if (isAbsenceDirty(item, absence)) {
            const type = getType();
            await store.dispatch(updateAbsence(asAbsence(item, absence, type))).unwrap();
        }
    } catch (error) {
        showSnack(`Unable to update absence ${formatScheduleError(error)}`, 'warning');
    }
}

const formatScheduleError = (error: unknown): string => {
    if (typeof error === 'string') {
        return error;
    }

    if (error instanceof Error) {
        return error.message;
    }

    if (error && typeof error === 'object') {
        const maybeResponse = error as { response?: { status?: number; data?: { message?: string } | string }; message?: string };
        if (typeof maybeResponse.response?.data === 'string') {
            return maybeResponse.response.data;
        }
        if (maybeResponse.response?.data?.message) {
            return maybeResponse.response.data.message;
        }
        if (maybeResponse.response?.status) {
            return `request failed with status ${maybeResponse.response.status}`;
        }
        if (maybeResponse.message) {
            return maybeResponse.message;
        }
    }

    return 'unknown error';
}

export const ItemsBatchApprove = async (items: TimeSheetItem[]): Promise<void> => {
    try {
        await ShiftBatchApprove(items.filter(x => x.timeshiftType === TimeSheetItemType.SHIFT));
        await AbsenceBatchApprove(items.filter(x => x.timeshiftType === TimeSheetItemType.ABSENCE));
    } catch (error) {
        showSnack(`Unable to approve scope of items. ${error}`, 'warning');
    }
}

export const ItemsBatchBill = async (items: TimeSheetItem[]): Promise<void> => {
    const toBill = items.filter(x => 
        x.timeshiftType === TimeSheetItemType.SHIFT 
        && x.isBillable
        && x.status !== TimeSheetItemStatus.BILLABLE);
    try {
        await ShiftBatchBillable(toBill);
    } catch (error) {
        showSnack(`Unable to bill shifts. ${error}`, 'warning');
    }
}

export const ItemsBatchDisapprove = async (items: TimeSheetItem[]): Promise<void> => {
    try {
        await ShiftBatchDisapprove(items.filter(x => x.timeshiftType === TimeSheetItemType.SHIFT));
        await AbsenceBatchDisapprove(items.filter(x => x.timeshiftType === TimeSheetItemType.ABSENCE));
    } catch (error) {
        showSnack(`Unable to disapprove items. ${error}`, 'warning');
    }
}

export const ItemsBatchUnbill = async (items: TimeSheetItem[]): Promise<void> => {
    try {
        await ShiftBatchUnbill(items.filter(x => x.timeshiftType === TimeSheetItemType.SHIFT && x.status === TimeSheetItemStatus.BILLABLE));
    } catch (error) {
        showSnack(`Unable to unbill shifts. ${error}`, 'warning');
    }
}

export const ItemBill = async (item: TimeSheetItem): Promise<void> => {
    if (!(item.timeshiftType === TimeSheetItemType.SHIFT 
        && item.isBillable
        && item.status !== TimeSheetItemStatus.BILLABLE)) {
        return;
    }

    try {
        await store.dispatch(shiftBillable({
            id: item.itemId,
            billed: true
        })).unwrap()
    } catch (error) {
        showSnack(`Unable to bill shift. ${error}`, 'warning');
    }
}

export const ItemUnbill = async (item: TimeSheetItem): Promise<void> => {
    try {

        if (!(item.timeshiftType === TimeSheetItemType.SHIFT 
            && item.status === TimeSheetItemStatus.BILLABLE)) {
            return;
        }
        
        await store.dispatch(shiftBillable({
            id: item.itemId,
            billed: false
        })).unwrap();
    } catch (error) {
        showSnack(`Unable to unbill shift. ${error}`, 'warning');
    }
}

export const ItemApprove = async (item: TimeSheetItem): Promise<void> => {
    try {
        if (item.timeshiftType === TimeSheetItemType.SHIFT) {
            await store.dispatch(shiftApprove({
                id: item.itemId,
                approve: true
            })).unwrap();
        } else {
            await store.dispatch(absenceApprove({
                id: item.itemId,
                approve: true
            })).unwrap();
        }
    } catch (error) {
        showSnack(`Unable to approve item. ${error}`, 'warning');
    }
}

export const ItemDisapprove = async (item: TimeSheetItem): Promise<void> => {
    try {
        if (item.timeshiftType === TimeSheetItemType.SHIFT) {
            await store.dispatch(shiftApprove({
                id: item.itemId,
                approve: false
            })).unwrap();
        } else {
            await store.dispatch(absenceApprove({
                id: item.itemId,
                approve: false
            })).unwrap();
        }
    } catch (error) {
        showSnack(`Unable to disapprove item. ${error}`, 'warning');
    }
}

const ShiftBatchApprove = async (items: TimeSheetItem[]): Promise<void> => {
    await store.dispatch(shiftBatchApprove({
        ids: items.map(x => (x.itemId)),
        approve: true
    })).unwrap()
}

const ShiftBatchDisapprove = async (items: TimeSheetItem[]): Promise<void> => {
    await store.dispatch(shiftBatchApprove({
        ids: items.map(x => (x.itemId)),
        approve: false
    })).unwrap()
}

const ShiftBatchBillable = async (items: TimeSheetItem[]): Promise<void> => {
    await store.dispatch(shiftBatchBillable({
        ids: items.map(x => (x.itemId)),
        billed: true
    })).unwrap()
}

const ShiftBatchUnbill = async (items: TimeSheetItem[]): Promise<void> => {
    await store.dispatch(shiftBatchBillable({
        ids: items.map(x => (x.itemId)),
        billed: false
    })).unwrap()
}

const AbsenceBatchApprove = async (items: TimeSheetItem[]): Promise<void> => {
    await store.dispatch(absenceBatchApprove({
        ids: items.map(x => (x.itemId)),
        approve: true
    })).unwrap()
}

const AbsenceBatchDisapprove = async (items: TimeSheetItem[]): Promise<void> => {
    await store.dispatch(absenceBatchApprove({
        ids: items.map(x => (x.itemId)),
        approve: false
    })).unwrap()
}
